/**
 * The dated-field model (1.Y deliverable 1), pure. A temporal field's values are dated rows: an
 * `EntityStateV1` for a scalar kind, a `LinkV1` for `doc_ref` and `doc_refs`. "Current" follows
 * 1.L-2's ruling (a) as the server's `currentWindow` does: a row's as-of window is
 * `[from.earliest, to.earliest)`, which on the wire is the possible span's start and the certain
 * span's end, unbounded on an open side; an `invalid` link or a row with an empty possible span is
 * never current, and with NOW unset nothing is. `planSince` orders the writes of a change "since"
 * an in-world date, each with what undoes it; `commit.ts` runs them.
 */
import { type CompiledCalendar, formatTick, formatTimePoint, toTicks } from '@codex/calendar';
import type {
  EntityStateV1,
  FieldDefV1,
  LinkBoundaryV1,
  LinkCreateV1,
  LinkTypeV1,
  LinkUpdateV1,
  LinkV1,
  StateCreateV1,
  StateUpdateV1,
} from '@codex/contracts';
import {
  ALREADY_ENDED,
  ALWAYS,
  aroundText,
  betweenText,
  LINK_TYPE_UNREAD,
  sinceText,
  startsTooEarly,
  UNKNOWN_DATE,
  UNTITLED,
  untilText,
} from './words.js';

export type DatedRow = EntityStateV1 | LinkV1;

const isLink = (row: DatedRow): row is LinkV1 => 'linkTypeId' in row;

/** Whether a field's values are links (`doc_ref`, `doc_refs`) rather than states. */
export const isRefField = (field: Pick<FieldDefV1, 'kind'>): boolean =>
  field.kind === 'doc_ref' || field.kind === 'doc_refs';

/** An as-of window `[lo, hi)`, a null side unbounded. */
export interface TickWindow {
  readonly lo: number | null;
  readonly hi: number | null;
}

/** The earliest tick of an authored time point, −∞ as null for one open below; undefined: unknown. */
function pointEarliest(
  calendar: CompiledCalendar | null | undefined,
  boundary: LinkBoundaryV1 | null,
): number | null | undefined {
  if (!calendar || boundary === null || !('timePoint' in boundary)) return undefined;
  const ticks = toTicks(calendar, boundary.timePoint);
  if (ticks.status === 'ok') return Math.min(ticks.lo, ticks.hi);
  if (ticks.status === 'open_hi') return ticks.lo;
  if (ticks.status === 'open_lo') return null;
  return undefined;
}

/**
 * A row's as-of window, or null when it is never current. Its start is the possible span's (the
 * `from` boundary's earliest instant); its end the certain span's (the `to` boundary's earliest).
 * Where the certain span is empty (fuzzy boundaries that overlap) the end is the `to` time point's
 * earliest tick through the calendar; for a placement edge it is not known here and the row is
 * not current.
 */
export function asOfWindow(row: DatedRow, calendar?: CompiledCalendar | null): TickWindow | null {
  if (isLink(row) && row.spanStatus === 'invalid') return null;
  const possible = row.possibleSpan;
  if (possible.empty) return null;
  const lo = possible.lo;
  const certain = row.certainSpan;
  let hi: number | null;
  if (!certain.empty) hi = certain.hi;
  else {
    const to = isLink(row) ? row.validTo : row.to;
    if (to === null) hi = null;
    else {
      const earliest = pointEarliest(calendar, to);
      if (earliest === undefined || earliest === null) return null;
      hi = earliest;
    }
  }
  if (lo !== null && hi !== null && !(lo < hi)) return null;
  return { lo, hi };
}

/** Whether a row is current at `now`: never when NOW is unset or the row has no window. */
export function isCurrentAt(
  row: DatedRow,
  now: number | null,
  calendar?: CompiledCalendar | null,
): boolean {
  if (now === null) return false;
  const window = asOfWindow(row, calendar);
  if (window === null) return false;
  return (window.lo === null || window.lo <= now) && (window.hi === null || now < window.hi);
}

const startOf = (row: DatedRow): number => {
  const span = row.possibleSpan;
  return span.empty || span.lo === null ? Number.NEGATIVE_INFINITY : span.lo;
};

/** 1.L-2's tie-break, best first: the latest start, then the latest `createdAt`, then the larger id. */
function newestFirst(a: DatedRow, b: DatedRow): number {
  const sa = startOf(a);
  const sb = startOf(b);
  if (sa !== sb) return sa > sb ? -1 : 1;
  if (a.createdAt !== b.createdAt) return a.createdAt > b.createdAt ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id > b.id ? -1 : 1;
}

export interface CurrentValues<T extends DatedRow> {
  readonly rows: readonly T[];
  /** NOW is unset: `rows` holds every live row (1.B-1's fallback). */
  readonly nowUnset: boolean;
}

/**
 * The rows a field shows now. A state's field is matched by `fieldDefId`; links are passed already
 * read for the field. A scalar shows at most one (the tie-break's best); references every current
 * link, in the order given. With NOW unset every row is returned, marked `nowUnset`.
 */
export function currentValues<T extends DatedRow>(
  rows: readonly T[],
  field: Pick<FieldDefV1, 'id' | 'kind'>,
  now: number | null,
  calendar?: CompiledCalendar | null,
): CurrentValues<T> {
  const own = rows.filter((row) => isLink(row) || row.fieldDefId === field.id);
  if (now === null) return { rows: own, nowUnset: true };
  const current = own.filter((row) => isCurrentAt(row, now, calendar));
  if (isRefField(field)) return { rows: current, nowUnset: false };
  const best = [...current].sort(newestFirst)[0];
  return { rows: best === undefined ? [] : [best], nowUnset: false };
}

// ------------------------------------------------------------------------------- the text

/** A state's value in words: a list joined, yes or no for a flag. */
export function valueText(value: EntityStateV1['value']): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** The document at the other end of a link from `docId`. */
export const otherEndOf = (link: LinkV1, docId: string): string =>
  link.sourceId === docId ? link.targetId : link.sourceId;

/** One side of a row's validity in words; null for an unbounded side. */
export function boundaryText(
  boundary: LinkBoundaryV1 | null,
  calendar: CompiledCalendar | null,
  edgeTick: number | null,
): string | null {
  if (boundary === null) return null;
  if ('timePoint' in boundary) {
    const point = boundary.timePoint;
    return (calendar && formatTimePoint(calendar, point)) || point.label || point.rawInput;
  }
  const date = calendar && edgeTick !== null ? formatTick(calendar, edgeTick, 'day') : null;
  return aroundText(date ?? UNKNOWN_DATE);
}

/** A row's `from` in words ("9 Ember 1042"), null when unbounded. */
export function fromTextOf(row: DatedRow, calendar: CompiledCalendar | null): string | null {
  const from = isLink(row) ? row.validFrom : row.from;
  const span = row.possibleSpan;
  return boundaryText(from, calendar, span.empty ? null : span.lo);
}

/** A row's `to` in words, null when unbounded. */
export function toTextOf(row: DatedRow, calendar: CompiledCalendar | null): string | null {
  const to = isLink(row) ? row.validTo : row.to;
  const span = row.certainSpan;
  return boundaryText(to, calendar, span.empty ? null : span.hi);
}

/** "Since A", "A to B", "Until B" or "Always". */
export function periodText(from: string | null, to: string | null): string {
  if (from !== null && to !== null) return betweenText(from, to);
  if (from !== null) return sinceText(from);
  if (to !== null) return untilText(to);
  return ALWAYS;
}

export interface HistoryRow {
  readonly id: string;
  readonly value: string;
  readonly fromText: string | null;
  readonly toText: string | null;
  /** The period in words: "Since <date>" for an open end. */
  readonly text: string;
}

/**
 * Every row, newest first (latest start, then latest `createdAt`, then larger id). A link's value
 * is the title of its other end from `docId`, from `titles`. Never throws.
 */
export function historyRows(
  rows: readonly DatedRow[],
  calendar: CompiledCalendar | null,
  titles: ReadonlyMap<string, string>,
  docId = '',
): HistoryRow[] {
  return [...rows].sort(newestFirst).map((row) => {
    const value = isLink(row)
      ? (titles.get(otherEndOf(row, docId)) ?? UNTITLED)
      : valueText(row.value);
    const fromText = fromTextOf(row, calendar);
    const toText = toTextOf(row, calendar);
    return { id: row.id, value, fromText, toText, text: periodText(fromText, toText) };
  });
}

// ------------------------------------------------------------------------------- the plan

/** Where a field's values are written. */
export interface DatedTarget {
  readonly docId: string;
  readonly field: FieldDefV1;
  /** The field's link type, for a reference field. */
  readonly linkType: Pick<LinkTypeV1, 'key' | 'symmetric'> | null;
}

/** What the GM asked for. */
export type DatedChange =
  /** A scalar's new value. */
  | { readonly kind: 'set'; readonly value: EntityStateV1['value'] }
  /** A `doc_ref`'s new page. */
  | { readonly kind: 'point'; readonly otherId: string }
  /** A further page of a `doc_refs`. */
  | { readonly kind: 'add'; readonly otherId: string }
  /** The end of one page of a `doc_refs`. */
  | { readonly kind: 'end'; readonly link: LinkV1 };

/** The "since" boundary, from 1.O-1's `whenToBoundary`, with its earliest tick when known. */
export interface Since {
  readonly boundary: LinkBoundaryV1;
  readonly earliest: number | null;
}

export type DatedStep =
  | {
      readonly op: 'states.update';
      readonly docId: string;
      readonly stateId: string;
      readonly body: StateUpdateV1;
      /** What the compensation writes back. */
      readonly restore: Pick<StateUpdateV1, 'to'>;
    }
  | { readonly op: 'states.create'; readonly docId: string; readonly body: StateCreateV1 }
  | {
      readonly op: 'links.update';
      /** The document whose path carries the link: its source. */
      readonly sourceId: string;
      readonly linkId: string;
      readonly body: LinkUpdateV1;
      readonly restore: Pick<LinkUpdateV1, 'validTo' | 'ongoing'>;
    }
  | { readonly op: 'links.create'; readonly sourceId: string; readonly body: LinkCreateV1 };

export type DatedPlan = { readonly steps: DatedStep[] } | { readonly refusal: string };

/** The first row the "since" starts before, if any. */
function startsBefore(rows: readonly DatedRow[], since: Since): DatedRow | null {
  if (since.earliest === null) return null;
  for (const row of rows) {
    const span = row.possibleSpan;
    if (!span.empty && span.lo !== null && since.earliest < span.lo) return row;
  }
  return null;
}

/** Whether a row has no end: no `to` (a state) or no `validTo` (a link, ongoing or not). */
const isOpenEnded = (row: DatedRow): boolean => (isLink(row) ? row.validTo : row.to) === null;

/** The row that starts first after the "since", the bound of the new value; null when none. */
function nextAfter(rows: readonly DatedRow[], since: Since): DatedRow | null {
  let next: DatedRow | null = null;
  for (const row of rows) {
    if (startsBefore([row], since) === null) continue;
    if (next === null || startOf(row) < startOf(next)) next = row;
  }
  return next;
}

function endState(state: EntityStateV1, since: Since): DatedStep {
  return {
    op: 'states.update',
    docId: state.documentId,
    stateId: state.id,
    body: { baseRv: state.rv, to: since.boundary },
    restore: { to: state.to },
  };
}

function endLink(link: LinkV1, since: Since): DatedStep {
  return {
    op: 'links.update',
    sourceId: link.sourceId,
    linkId: link.id,
    body: { baseRv: link.rv, validTo: since.boundary },
    restore: link.ongoing ? { validTo: null, ongoing: true } : { validTo: link.validTo },
  };
}

/** A new link from this document (`out`, symmetric) or to it (`in`), valid from the "since". */
function newLink(
  target: DatedTarget,
  otherId: string,
  since: Since,
  until: LinkBoundaryV1 | null = null,
): DatedStep | null {
  const { linkType, field, docId } = target;
  if (linkType === null) return null;
  const direction = 'direction' in field.config ? field.config.direction : 'out';
  const outward = linkType.symmetric || direction === 'out';
  return {
    op: 'links.create',
    sourceId: outward ? docId : otherId,
    body: {
      linkTypeKey: linkType.key,
      targetId: outward ? otherId : docId,
      validFrom: since.boundary,
      ...(until === null ? {} : { validTo: until }),
    },
  };
}

/**
 * The ordered writes of a change since `since`: the rows that hold at the "since" end first, then
 * the new one starts, both at the same boundary. `rows` is every row of the field; `shown` the
 * rows it shows at NOW (null while NOW is unset).
 *
 * - A "since" whose earliest tick falls before a shown row's start is refused with no step.
 * - A scalar or `doc_ref` value is replaced by what holds at the "since"'s earliest tick. Where a
 *   later row of the field starts after the "since", the new value ends where the first of them
 *   starts (its `to` is that row's `from`), so no instant ever has two values; a since past every
 *   row gives an open-ended value. With the earliest tick unknown the shown rows end (with NOW
 *   unset, the open-ended ones) and the server judges.
 * - `doc_refs` Add is one create; End is one update, refused before the link began or when it has
 *   already ended.
 */
export function planSince(
  target: DatedTarget,
  rows: readonly DatedRow[],
  change: DatedChange,
  since: Since,
  calendar: CompiledCalendar | null = null,
  shown: readonly DatedRow[] | null = null,
): DatedPlan {
  if (change.kind === 'end') {
    const early = startsBefore([change.link], since);
    if (early !== null)
      return { refusal: startsTooEarly(fromTextOf(early, calendar) ?? UNKNOWN_DATE) };
    if (!isOpenEnded(change.link)) return { refusal: ALREADY_ENDED };
    return { steps: [endLink(change.link, since)] };
  }
  if (change.kind !== 'add') {
    const early = startsBefore(shown ?? [], since);
    if (early !== null)
      return { refusal: startsTooEarly(fromTextOf(early, calendar) ?? UNKNOWN_DATE) };
  }
  if (change.kind === 'set') {
    const own = rows.filter(
      (row): row is EntityStateV1 => !isLink(row) && row.fieldDefId === target.field.id,
    );
    const next = nextAfter(own, since);
    const ending = endingAt(own, shown, since, calendar);
    const steps: DatedStep[] = ending.map((state) => endState(state, since));
    steps.push({
      op: 'states.create',
      docId: target.docId,
      body: {
        fieldDefId: target.field.id,
        value: change.value,
        from: since.boundary,
        ...(next === null ? {} : { to: (next as EntityStateV1).from }),
      },
    });
    return { steps };
  }
  const links = rows.filter(isLink);
  const next = change.kind === 'point' ? (nextAfter(links, since) as LinkV1 | null) : null;
  const create = newLink(target, change.otherId, since, next?.validFrom ?? null);
  if (create === null) return { refusal: LINK_TYPE_UNREAD };
  if (change.kind === 'add') return { steps: [create] };
  const ends = endingAt(links, shown, since, calendar).map((link) => endLink(link, since));
  return { steps: [...ends, create] };
}

/** The rows a new value replaces: those holding at the "since" (see `planSince`). */
function endingAt<T extends DatedRow>(
  rows: readonly T[],
  shown: readonly DatedRow[] | null,
  since: Since,
  calendar: CompiledCalendar | null,
): T[] {
  if (since.earliest !== null) {
    const at = since.earliest;
    return rows.filter((row) => isCurrentAt(row, at, calendar));
  }
  if (shown !== null) return rows.filter((row) => shown.some((s) => s.id === row.id));
  return rows.filter(isOpenEnded);
}
